import dayjs from "dayjs";
import IDSAR from "../models/dsar/IDSAR";
import IPerson from "../models/IPerson";
import IStaticValue from "../models/IStaticValue";
import networkService from "./networkService";

class SARService {
    
    /**
     * @description Fetches the subject access requests submitted by the current user
     */
    async getSARs() : Promise<IDSAR[]> {
        const result = await networkService.get<IDSAR[]>("sars");

        if (result.isSuccessful) {
            return result.data as IDSAR[];
        }

        throw result.data;
    }

    /**
     * @description Fetches a subject access request
     * @param {string} id The ID of the subject access request to fetch
     */
    async getSAR(id: string) : Promise<IDSAR> {
        const result = await networkService.get<IDSAR>(`sar/${id}`);

        if (result.isSuccessful) {
            return result.data as IDSAR;
        }

        throw result.data;
    }

    /**
     * @description Creates a subject access request
     * @param {IDSAR} sar The subject access request to create
     * @returns {string} The ID of the created subject access request
     */
    async createSAR(sar: IDSAR) : Promise<string> {
        sar.custodianContractStart = !!sar.custodianContractStart
            ? dayjs(sar.custodianContractStart).toISOString()
            : null!;

        sar.custodianContractEnd = !!sar.custodianContractEnd
            ? dayjs(sar.custodianContractEnd).toISOString()
            : null!;

        sar.dataSearchStart = !!sar.dataSearchStart
            ? dayjs(sar.dataSearchStart).toISOString()
            : null!;

        sar.dataSearchEnd = !!sar.dataSearchEnd
            ? dayjs(sar.dataSearchEnd).toISOString()
            : null!;

        sar.emailSearchStart = !!sar.emailSearchStart
            ? dayjs(sar.emailSearchStart).toISOString()
            : null!;

        sar.emailSearchEnd = !!sar.emailSearchEnd
            ? dayjs(sar.emailSearchEnd).toISOString()
            : null!;

        const result = await networkService.put<IDSAR, string>("sar", sar);

        if (result.isSuccessful) {
            return result.data as string;
        }

        throw result.data;
    }

    async approveSAR(id: string) : Promise<boolean> {
        const result = await networkService.patch<void, void>(`sar/${id}/approve`);
        return result.isSuccessful;
    }

    async rejectSAR(id: string, reason: string) : Promise<boolean> {
        const result = await networkService.patch<string, void>(`sar/${id}/reject`, reason);
        return result.isSuccessful;
    }

    async appealSAR(id: string, reason: string) : Promise<boolean> {
        const result = await networkService.patch<string, void>(`sar/${id}/appeal`, reason);
        return result.isSuccessful;
    }

    async requestAdditionalInfo(id: string, infoRequested: string) : Promise<boolean> {
        const result = await networkService.patch<string, void>(`sar/${id}/request-additional-info`, infoRequested);
        return result.isSuccessful;
    }

    async submitAdditionalInfo(sar: IDSAR) : Promise<boolean> {
        const result = await networkService.patch<IDSAR, void>(`sar/${sar.id}/submit-additional-info`, sar);
        return result.isSuccessful;
    }

    async getAuthorisers() : Promise<IPerson[]> {
        const result = await networkService.get<IPerson[]>("sar/authorisers");

        if (result.isSuccessful) {
            return result.data as IPerson[];
        }

        throw result.data;
    }

    async getCustodianTypes() : Promise<IStaticValue[]> {
        const result = await networkService.get<IStaticValue[]>("sar/custodian-types");

        if (result.isSuccessful) {
            return result.data as IStaticValue[];
        }

        throw result.data;
    }

    async getSearchTypes() : Promise<IStaticValue[]> {
        const result = await networkService.get<IStaticValue[]>("sar/search-types");

        if (result.isSuccessful) {
            return result.data as IStaticValue[];
        }

        throw result.data;
    }

}

const sarService = new SARService();
export default sarService;